import type { QueryClient } from '@tanstack/react-query';

export const PORTAL_PAYMENT_PENDING_KEY = 'clinio:portal-payment-pending';

export const DEFAULT_SETTLEMENT_DELAYS_MS = [400, 800, 1200, 1600, 2000, 2000] as const;

export const PORTAL_SETTLEMENT_MAX_AGE_MS = 30_000;

const STAFF_INVOICE_RETURN =
  /^\/invoices\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface PortalPaymentPending {
  payToken: string;
  appointmentId?: string | null;
  startedAt: number;
}

export interface SettledInvoice {
  status: string;
  appointment_id: string | null;
}

export function isSafePortalPayReturnPath(pathname: string): boolean {
  return pathname === '/portal/invoices' || pathname === '/portal/appointments';
}

export function isSafePayReturnPath(pathname: string): boolean {
  return isSafePortalPayReturnPath(pathname) || STAFF_INVOICE_RETURN.test(pathname);
}

export function resolveSafePayReturnPath(
  raw: string | null,
  storedByToken: string | null,
): string | null {
  for (const candidate of [raw, storedByToken]) {
    if (!candidate) continue;
    let path = candidate;
    try {
      path = decodeURIComponent(candidate);
    } catch {
      path = candidate;
    }
    if (path.startsWith('//') || path.includes('://')) continue;
    const pathname = path.split('?')[0];
    if (isSafePayReturnPath(pathname)) return pathname;
  }
  return null;
}

function paymentStorage(): Storage | null {
  try {
    if (typeof sessionStorage === 'undefined') return null;
    return sessionStorage;
  } catch {
    return null;
  }
}

export function markPortalPaymentPending(
  pending: Omit<PortalPaymentPending, 'startedAt'> & { startedAt?: number },
): void {
  const storage = paymentStorage();
  if (!storage) return;
  const record: PortalPaymentPending = {
    payToken: pending.payToken,
    appointmentId: pending.appointmentId ?? null,
    startedAt: pending.startedAt ?? Date.now(),
  };
  storage.setItem(PORTAL_PAYMENT_PENDING_KEY, JSON.stringify(record));
}

export function readPortalPaymentPending(): PortalPaymentPending | null {
  const storage = paymentStorage();
  if (!storage) return null;
  const raw = storage.getItem(PORTAL_PAYMENT_PENDING_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<PortalPaymentPending>;
    if (typeof parsed.payToken !== 'string' || parsed.payToken.length < 8) return null;
    if (typeof parsed.startedAt !== 'number') return null;
    return {
      payToken: parsed.payToken,
      appointmentId: parsed.appointmentId ?? null,
      startedAt: parsed.startedAt,
    };
  } catch {
    return null;
  }
}

export function clearPortalPaymentPending(): void {
  paymentStorage()?.removeItem(PORTAL_PAYMENT_PENDING_KEY);
}

export function clearPortalPaymentPendingIfToken(payToken: string | undefined): void {
  if (!payToken) return;
  const pending = readPortalPaymentPending();
  if (pending?.payToken === payToken) clearPortalPaymentPending();
}

export function isInvoiceSettledPaid(invoice: SettledInvoice | null | undefined): boolean {
  return invoice?.status === 'paid';
}

export async function waitForBackendPaymentSettlement(options: {
  payToken: string;
  loadInvoice: (payToken: string) => Promise<SettledInvoice | null>;
  delaysMs?: readonly number[];
  sleep?: (ms: number) => Promise<void>;
}): Promise<SettledInvoice | null> {
  const delays = options.delaysMs ?? DEFAULT_SETTLEMENT_DELAYS_MS;
  const sleep =
    options.sleep ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));

  let last: SettledInvoice | null = null;
  for (let attempt = 0; attempt <= delays.length; attempt += 1) {
    last = await options.loadInvoice(options.payToken);
    if (isInvoiceSettledPaid(last)) return last;
    if (attempt < delays.length) {
      await sleep(delays[attempt]);
    }
  }
  return last;
}

export async function invalidatePortalPaymentQueries(qc: QueryClient): Promise<void> {
  await Promise.all([
    qc.invalidateQueries({ queryKey: ['portal', 'appointments'] }),
    qc.invalidateQueries({ queryKey: ['portal', 'invoices'] }),
  ]);
}

export function shouldContinuePortalSettlementPoll(input: {
  pending: PortalPaymentPending | null;
  confirmedPaid: boolean;
  nowMs?: number;
  maxAgeMs?: number;
}): boolean {
  if (!input.pending || input.confirmedPaid) return false;
  const now = input.nowMs ?? Date.now();
  const maxAge = input.maxAgeMs ?? PORTAL_SETTLEMENT_MAX_AGE_MS;
  return now - input.pending.startedAt < maxAge;
}
